import React, { useRef, useEffect, useMemo } from 'react';
import {
  Plate,
  PlateProvider,
  isElementEmpty,
  serializeHtml,
  createPlugins,
} from '@udecode/plate';
import clsx from 'clsx';
import { focusEditor } from '@udecode/slate-react';

import deserializeString from './utils/deserializeString';
import { convertMergeFields } from './plugins/merge-fields/convertMergeFields';
import { plugins } from './contexts/editorField';
import { FieldEditorToolbar as Toolbar } from './components/toolbar/FieldEditorToolbar';
import { createMergeFieldsPlugin } from './plugins/merge-fields/createMergeFieldsPlugin';

import './plate.scss';

const _editableProps = {
  placeholder: 'Type...',
  style: { minHeight: '100px' },
  className: 'editor-field',
  tabIndex: 0,
};

const PlateEditorField = ({
  onChange,
  setEditorValue,
  onChangeMode = 'json',
  labelHidden = true,
  fieldLabel,
  required = false,
  error = false,
  setError,
  value,
  editableProps,
  mergeFields,
  allowFormatting = true,
  isFormSubmitted,
}) => {
  const editorRef = useRef(null);

  const initialValue = useMemo(() => {
    return typeof(value) === 'string' ? deserializeString(convertMergeFields(value, mergeFields)) : value;
  }, [value]);

  const handleChange = (editorValue) => {
    if (editorRef.current) {
      const errorNode = checkForErrors(editorValue);

      if (errorNode) {
        const { type } = errorNode;
        if (type && type === 'merge-field') {
          setError('One or more merge fields could not be reconciled.');
        }
      } else {
        setError(false);
      }
      const isAstChange = editorRef.current.operations.some(op => op.type !== 'set_selection');
      if (!isAstChange) {
        return;
      }
    }
    // We want to be able to validate if the wysiwyg field is empty.
    const isEmpty = Array.isArray(editorValue) && editorValue.every(node => isElementEmpty(editorRef.current, node));

    if (isEmpty && isFormSubmitted) {
      setError(true);
    }

    if (isEmpty) {
      onChange('');
      return;
    }

    setEditorValue(editorValue);

    if (onChangeMode === 'html') {
      const html = serializeHtml(
        editorRef.current,
        {
          nodes: editorValue,
          convertNewLinesToHtmlBr: true,
          preserveClassNames: ['slate'],
        },
      );
      onChange(html);
    } else {
      onChange(editorValue);
    }
  };

  let addtPlugins = [];
  if (mergeFields) {
    addtPlugins = createPlugins([
      createMergeFieldsPlugin({
        options: {
          mergeFields,
        },
      }),
    ]);
  }

  // Need to manually update the editor because it won't react to the props changing.
  useEffect(() => {
    if (editorRef?.current) {
      editorRef.current.children = initialValue;
      editorRef.current.onChange();
    }
  }, [initialValue]);

  return (
    <div className="form-item form-item--wysiwyg wysiwyg-editor-field ap-slate-editor">
      <label
        className={clsx(
          'form-item__label',
          required && 'form-item__label--required',
          labelHidden && 'visually-hidden',
        )}
        onClick={() => focusEditor(editorRef.current)}
      >
        {fieldLabel}
        {required && (
          <span className="required">*</span>
        )}
      </label>
      <div className={clsx('form-item__wysiwyg-field', error && 'error')}>
        <PlateProvider
          editorRef={editorRef}
          initialValue={initialValue}
          normalizeInitialValue
          plugins={[...plugins, ...addtPlugins]}
          onChange={handleChange}
        >
          { allowFormatting && (
            <div
              className={clsx('plate-toolbar__wrapper')}
            >
              <Toolbar
                disableToolbarTab
                condensedAlignment
              />
            </div>
          )}
          <Plate
            editableProps={{ ..._editableProps, ...editableProps }}
          />
        </PlateProvider>
      </div>
    </div>
  );
};

/**
 * Finds the first error attribute among slate nodes and returns the type.
 * @param {Array} nodes
 * @returns node
 */
const checkForErrors = (nodes) => {
  let innerFound;
  const found = nodes.find(node => {
    const { error, children } = node;

    if (error) return true;

    if (children) {
      innerFound = checkForErrors(children);
    }
    return innerFound;
  });

  if (innerFound) return innerFound;
  return found;
};

export default PlateEditorField;
